import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const DEFAULT_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function toMin(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return 0;
  const [h, m] = hhmm.split(":");
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
}

function diffHours(from, to) {
  return Math.max(0, (toMin(to) - toMin(from)) / 60);
}

/** Validate the whole breaks list against the given shift.
 *  Returns { errors: string[], perRow: (string|null)[] }. */
export function validateBreaks(breaks, startTime, endTime, t) {
  const errors = [];
  const perRow = breaks.map(() => null);
  const shiftS = toMin(startTime), shiftE = toMin(endTime);
  const byDay = new Map();
  const seen = new Set();
  breaks.forEach((b, idx) => {
    const s = toMin(b.start), e = toMin(b.end);
    if (!b.start || !b.end || e <= s) {
      perRow[idx] = t("breaks.error_range");
      return;
    }
    if (shiftE > shiftS && (s < shiftS || e > shiftE)) {
      perRow[idx] = t("breaks.error_outside", { start: startTime, end: endTime });
      return;
    }
    const days = b.days || [];
    if (!days.length) {
      perRow[idx] = t("breaks.error_no_days");
      return;
    }
    const key = `${b.start}-${b.end}-${[...days].sort().join(",")}`;
    if (seen.has(key)) { perRow[idx] = t("breaks.error_duplicate"); return; }
    seen.add(key);
    for (const d of days) {
      const list = byDay.get(d) || [];
      for (const [os, oe, oi] of list) {
        if (!(e <= os || s >= oe)) {
          perRow[idx] = t("breaks.error_overlap");
          if (perRow[oi] === null) perRow[oi] = t("breaks.error_overlap");
        }
      }
      list.push([s, e, idx]);
      byDay.set(d, list);
    }
  });
  perRow.forEach((m) => m && errors.push(m));
  return { errors: [...new Set(errors)], perRow };
}

/** Compute effective daily hours from a shift + breaks list.
 *  If `dow` is given, only breaks that apply to that weekday are subtracted.
 *  Otherwise returns an average shift minus average breaks (used as a hint). */
export function effectiveDailyHours({ start_time, end_time, breaks = [] }, dow = null) {
  const shift = diffHours(start_time, end_time);
  const applies = (b) => dow === null ? true : (b.days || []).includes(dow);
  const breakHrs = breaks.filter(applies)
    .reduce((s, b) => s + diffHours(b.start, b.end), 0);
  return Math.max(0, Math.round((shift - breakHrs) * 100) / 100);
}

export default function BreaksEditor({
  value = [], onChange, workingDays = [0, 1, 2, 3, 4],
  startTime = "08:00", endTime = "17:00", disabled = false,
  hint, emptyHint,
}) {
  const { t } = useTranslation();

  const breaks = value || [];
  const { perRow } = useMemo(
    () => validateBreaks(breaks, startTime, endTime, t),
    [breaks, startTime, endTime, t]
  );

  const patch = (i, fields) => onChange(breaks.map((b, k) => k === i ? { ...b, ...fields } : b));
  const remove = (i) => onChange(breaks.filter((_, k) => k !== i));
  const addRow = () => onChange([
    ...breaks,
    { start: "12:00", end: "12:30", days: [...workingDays] },
  ]);

  return (
    <div className="space-y-2" data-testid="breaks-editor">
      {hint && <p className="text-xs text-zinc-500">{hint}</p>}
      {breaks.length === 0 && emptyHint && (
        <p className="text-xs text-zinc-500 italic" data-testid="breaks-empty-hint">{emptyHint}</p>
      )}
      {breaks.map((b, i) => (
        <div key={i} className="rounded-lg border border-[#2E3241] p-3 space-y-2 bg-[#171B24]"
             data-testid={`break-row-${i}`}>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
                {t("settings.break_row_start")}
              </div>
              <Input type="time" value={b.start || ""} disabled={disabled}
                onChange={(e) => patch(i, { start: e.target.value })}
                className="bg-[#171B24] border-[#2E3241] font-mono-stat"
                data-testid={`break-start-${i}`} />
            </div>
            <div className="col-span-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
                {t("settings.break_row_end")}
              </div>
              <Input type="time" value={b.end || ""} disabled={disabled}
                onChange={(e) => patch(i, { end: e.target.value })}
                className="bg-[#171B24] border-[#2E3241] font-mono-stat"
                data-testid={`break-end-${i}`} />
            </div>
            <div className="col-span-5">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
                {t("settings.break_row_days")}
              </div>
              <div className="flex flex-wrap gap-1">
                {DEFAULT_DAY_KEYS.map((key, dow) => {
                  const active = (b.days || []).includes(dow);
                  return (
                    <button key={dow} type="button" disabled={disabled}
                      onClick={() => {
                        const next = active
                          ? (b.days || []).filter((d) => d !== dow)
                          : [...(b.days || []), dow].sort();
                        patch(i, { days: next });
                      }}
                      className={
                        "px-2 py-1 rounded text-xs font-semibold border transition-colors " +
                        (active
                          ? "bg-blue-500/20 border-blue-400/50 text-blue-200"
                          : "bg-transparent border-[#2E3241] text-zinc-400 hover:text-zinc-200")
                      }
                      data-testid={`break-day-${i}-${dow}`}>
                      {t(`common.day_short_${key}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              {!disabled && (
                <Button variant="ghost" size="icon" onClick={() => remove(i)}
                  className="text-zinc-400 hover:text-red-400 h-8 w-8"
                  data-testid={`break-delete-${i}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          {perRow[i] && (
            <p className="text-xs text-red-400" data-testid={`break-error-${i}`}>{perRow[i]}</p>
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="button" variant="ghost" onClick={addRow}
          className="text-blue-400 hover:bg-[#2A2F3A]" data-testid="breaks-add">
          <Plus className="h-4 w-4 mr-1" /> {t("settings.add_break")}
        </Button>
      )}
    </div>
  );
}
